/**
 * ч.920 (рішення власника): попап підбору товару для РАХУНКУ і ЗАМОВЛЕННЯ
 * ПОКУПЦЯ. Окремий компонент — спільний NomenclaturePickerModal (20 документів)
 * лишається без змін.
 *
 * - серверна пагінація + сортування (ag-Grid Infinite, Закон дизайну #1) —
 *   GET /reports/sellable-picker;
 * - колонки: На складі → В дорозі → В резерві → Замовлено → Баланс; три
 *   середні показуються ЛИШЕ коли стоїть їхня галка;
 * - галка «лише з залишком» вмикає можливість трьох інших (вони розширюють
 *   вибірку: з залишком АБО в дорозі АБО …); без неї — увесь довідник;
 * - галки цін: типи цін користувача; закупівельна = ціна ОСТАННЬОЇ закупівлі.
 */
import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { GridMoreRightButton, useAgMoreRight } from '@/shared/components/GridMoreRight';
import type { ColDef, GridApi } from 'ag-grid-community';
import { refs } from '@/api/refs';
import { reports, type GoodsBalanceRow, type SellablePickerRow } from '@/api/documents';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { uiPrefs } from '@/store/uiPrefs';
import { showToastImperative } from '@/components/ToastProvider';

// ч.924: налаштування попапа (галки + обрані колонки цін) — per-user, кнопкою
// «зберегти налаштування» (серверні ui-prefs, як ширини колонок таблиць).
const DEFAULT_PREFS_KEY = 'picker:customer-goods';
interface PickerPrefs {
  onlyWithStock: boolean; transit: boolean; reserved: boolean; ordered: boolean;
  priceTypeIds: string[];
  /** ч.926: ширини колонок (colId → px) — зберігаються тією ж кнопкою. */
  widths?: Record<string, number>;
}

interface Props {
  organizationId: string;
  documentDate: string;
  defaultWarehouseId?: string | null;
  /** ч.923: клік по ЦІНІ → price = ця ціна; клік по рядку → без ціни. */
  onPick: (row: GoodsBalanceRow, price?: string) => void;
  /** ч.927 (реалізація): мультивибір галочками + «додати обрані (N)»; клік по
   *  ціні, як і раніше, додає ОДИН товар з цією ціною. */
  multiSelect?: boolean;
  onPickMany?: (rows: GoodsBalanceRow[]) => void;
  /** ч.927 (реалізація): реалізувати можна ЛИШЕ те, що є на складі — рядок без
   *  додатного залишку сірий і не обирається (бекенд теж блокує проведення). */
  requireStock?: boolean;
  /** Окремий ключ збережених налаштувань (рахунок/замовлення vs реалізація). */
  prefsKey?: string;
  onClose: () => void;
}

// ч.921: спосіб розрахунку закупівельної ціни задається у типі цін.
const PURCHASE_METHOD_TIP: Record<string, string> = {
  LAST: 'Ціна останньої закупівлі',
  AVERAGE: 'Середня ціна закупівель за 12 місяців (зважена за кількістю)',
  MAX: 'Максимальна ціна закупівлі за 12 місяців',
};

const fmtQ = (v: string | null | undefined) => {
  const n = Number(v ?? 0);
  return n === 0 ? '' : n.toLocaleString('uk-UA', { maximumFractionDigits: 3 });
};
const fmtP = (v: string | null | undefined) => (v == null || v === '' ? ''
  : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export default function NomenclatureAvailabilityPicker({
  organizationId, documentDate, defaultWarehouseId, onPick, onClose,
  multiSelect = false, onPickMany, requireStock = false, prefsKey = DEFAULT_PREFS_KEY,
}: Props) {
  const [warehouseId, setWarehouseId] = useState<string>(defaultWarehouseId ?? '');
  const [kindId, setKindId] = useState('');
  const [search, setSearch] = useState('');
  // Збережені користувачем налаштування (якщо є) — початковий стан галок.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saved = useMemo(() => uiPrefs.get<PickerPrefs>(prefsKey), []);
  // ч.927: мультивибір — ключ «склад-номенклатура» → рядок.
  const selectedRef = useRef<Map<string, GoodsBalanceRow>>(new Map());
  const [selectedCount, setSelectedCount] = useState(0);
  const gridApiRef = useRef<GridApi<SellablePickerRow> | null>(null);
  const more = useAgMoreRight<SellablePickerRow>(); // ч.1316: «ще колонки →»
  // Збережені ширини — у ref: після «зберегти» нові колонки (галки цін) одразу
  // беруть актуальні значення, без перезавантаження попапа.
  const widthsRef = useRef<Record<string, number>>(saved?.widths ?? {});
  const [onlyWithStock, setOnlyWithStock] = useState(saved?.onlyWithStock ?? true);
  // Три галки доступні лише разом із першою; «в дорозі» увімкнена одразу —
  // товар, що їде від постачальника, можна виставляти в рахунок.
  const [chkTransit, setChkTransit] = useState(saved?.transit ?? true);
  const [chkReserved, setChkReserved] = useState(saved?.reserved ?? false);
  const [chkOrdered, setChkOrdered] = useState(saved?.ordered ?? false);
  const [priceTypeIdsRaw, setPriceTypeIds] = useState<string[]>(saved?.priceTypeIds ?? []);
  // ч.924: без «лише з залишком» лишаються тільки базові колонки (до «На складі»):
  // «В дорозі … Баланс» і всі ціни ховаються, їхні галки неактивні.
  const priceTypeIds = useMemo(() => (onlyWithStock ? priceTypeIdsRaw : []),
    [onlyWithStock, priceTypeIdsRaw]);
  const showTransit = onlyWithStock && chkTransit;
  const showReserved = onlyWithStock && chkReserved;
  const showOrdered = onlyWithStock && chkOrdered;

  const warehousesQ = useQuery({
    queryKey: ['warehouses-for-picker', organizationId],
    queryFn: () => refs.warehouses(organizationId),
    enabled: !!organizationId,
  });
  const kindsQ = useQuery({
    queryKey: ['nomenclature-kinds'], queryFn: refs.nomenclatureKinds.list, staleTime: 300_000,
  });
  const priceTypesQ = useQuery({
    queryKey: ['price-types'], queryFn: refs.priceTypes.list, staleTime: 300_000,
  });
  const priceTypes = priceTypesQ.data ?? [];
  // ч.1229: збережені типи цін — у профілі користувача, а типи цін у кожного клієнта свої:
  // у запит ідуть лише ті, що є в цьому клієнті.
  const priceKey = priceTypeIds.filter((id) => priceTypes.some((t) => t.id === id)).join(',');

  const { datasource, total } = useInfiniteGridDatasource<SellablePickerRow>(
    async (limit, offset, sort) => {
      if (!organizationId || !documentDate) return { items: [], total: 0 };
      return reports.sellablePicker({
        organization_id: organizationId, on_date: documentDate,
        warehouse_id: warehouseId || undefined, kind_id: kindId || undefined,
        only_kind: 'goods', q: search.trim() || undefined,
        with_stock: onlyWithStock, with_transit: showTransit,
        with_reserved: showReserved, with_ordered: showOrdered,
        price_type_ids: priceKey || undefined,
        sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
      });
    },
    [organizationId, documentDate, warehouseId, kindId, search, onlyWithStock,
      showTransit, showReserved, showOrdered, priceKey],
  );

  const cols = useMemo<ColDef<SellablePickerRow>[]>(() => {
    const num = (field: 'quantity' | 'in_transit' | 'reserved' | 'ordered', headerName: string,
      color: string, tip?: string): ColDef<SellablePickerRow> => ({
      field, headerName, headerTooltip: tip, width: 105,
      cellStyle: { textAlign: 'right', color },
      valueFormatter: (p) => fmtQ(p.value as string),
    });
    const out: ColDef<SellablePickerRow>[] = [
      ...(multiSelect ? [{
        colId: '_sel', headerName: '', width: 40, sortable: false, resizable: false,
        cellStyle: { textAlign: 'center' },
        cellRenderer: (p: { data?: SellablePickerRow }) => (p.data
          ? <input type="checkbox" readOnly style={{ width: 'auto', pointerEvents: 'none' }}
              checked={selectedRef.current.has(`${p.data.warehouse_id ?? ''}-${p.data.nomenclature_id}`)} />
          : ''),
      } as ColDef<SellablePickerRow>] : []),
      { field: 'nomenclature_code', headerName: 'Код', width: 100 },
      { field: 'nomenclature_name', headerName: 'Назва', flex: 1, minWidth: 220 },
      { field: 'uom_short', headerName: 'Од.', width: 64, cellStyle: { textAlign: 'center', color: 'var(--text-secondary)' } },
      { field: 'warehouse_name', headerName: 'Склад', width: 140 },
      { ...num('quantity', 'На складі', '#0f172a', 'Залишок на складі рядка'),
        cellStyle: { textAlign: 'right', fontWeight: 600 } },
    ];
    if (showTransit) out.push(num('in_transit', 'В дорозі', '#15803d', 'Замовлено постачальникам, ще не отримано (по організації)'));
    if (showReserved) out.push(num('reserved', 'В резерві', '#b45309', 'Закріплено за проведеними рахунками (на складі рядка)'));
    if (showOrdered) out.push(num('ordered', 'Замовлено', '#7c3aed', 'Обіцяно покупцям без резерву: замовлення + непокрита частина рахунків (по організації)'));
    if (onlyWithStock) out.push({
      field: 'balance', headerName: 'Баланс', width: 110,
      headerTooltip: 'На складах + в дорозі − в резерві − замовлено (по організації). Мінус — непокритий попит',
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 600,
        color: Number(p.value ?? 0) < 0 ? '#dc2626' : '#0f172a' }),
      valueFormatter: (p) => {
        const n = Number(p.value ?? 0);
        return n.toLocaleString('uk-UA', { maximumFractionDigits: 3 });
      },
    });
    for (const pt of priceTypes.filter((t) => priceTypeIds.includes(t.id))) {
      out.push({
        colId: `price_${pt.id}`, headerName: pt.name, width: 120,
        headerTooltip: pt.is_purchase ? PURCHASE_METHOD_TIP[pt.purchase_calc_method ?? 'LAST']
          : pt.kind === 'DYNAMIC' ? `Розрахункова: базова ціна × (1 + ${pt.markup_percent ?? 0}%)` : undefined,
        // ч.923 (рішення власника): ціна КЛІКАБЕЛЬНА — клік по ній додає товар у
        // ТЧ саме з цією ціною (клік деінде в рядку — без ціни).
        cellStyle: { textAlign: 'right', color: 'var(--primary)', textDecoration: 'underline',
          cursor: 'pointer' },
        valueGetter: (p) => p.data?.prices?.[pt.id] ?? null,
        valueFormatter: (p) => fmtP(p.value as string | null),
      });
    }
    // ч.926: збережена користувачем ширина перекриває типову (для «Назви» —
    // замість flex, інакше ag-Grid ігнорував би width).
    return out.map((c) => {
      const w = widthsRef.current[(c.colId ?? c.field) as string];
      if (!w) return c;
      const { flex: _flex, ...rest } = c;
      return { ...rest, width: w };
    });
  }, [showTransit, showReserved, showOrdered, priceTypes, priceTypeIds, onlyWithStock, multiSelect]);

  const chk = (label: string, val: boolean, set: (v: boolean) => void, disabled = false, title?: string) => (
    <label key={label} title={title}
      style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
        cursor: disabled ? 'not-allowed' : 'pointer', color: disabled ? 'var(--muted)' : undefined }}>
      <input type="checkbox" checked={val && !disabled} disabled={disabled}
        onChange={(e) => set(e.target.checked)} style={{ width: 'auto' }} />
      {label}
    </label>
  );

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: '94%', maxWidth: 1200, maxHeight: '90vh', background: 'var(--bg-panel)',
          borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)',
          display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Додати товар</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {/* ч.924/ч.925: галки і обрані колонки цін — зберігаються для користувача;
                кнопка — синя, у шапці перед хрестиком закриття (рішення власника). */}
            <button type="button" className="primary" style={{ whiteSpace: 'nowrap' }}
              title="Запамʼятати галки, колонки цін і ширини колонок — наступного разу попап відкриється так само"
              onClick={() => {
                // Ширини: поточний стан таблиці + раніше збережені для колонок,
                // яких зараз не видно (їхні галки вимкнені).
                const widths: Record<string, number> = { ...widthsRef.current };
                for (const c of gridApiRef.current?.getColumnState() ?? []) {
                  if (c.colId && typeof c.width === 'number') widths[c.colId] = Math.round(c.width);
                }
                widthsRef.current = widths;
                const prefs: PickerPrefs = {
                  onlyWithStock, transit: chkTransit, reserved: chkReserved, ordered: chkOrdered,
                  priceTypeIds: priceTypeIdsRaw, widths,
                };
                uiPrefs.set(prefsKey, prefs);
                showToastImperative({ type: 'success', message: 'налаштування підбору збережено', durationMs: 5000 });
              }}>
              зберегти налаштування
            </button>
            {multiSelect && (
              <button type="button" className="primary" disabled={selectedCount === 0}
                onClick={() => { onPickMany?.([...selectedRef.current.values()]); onClose(); }}>
                додати обрані ({selectedCount})
              </button>
            )}
            <button onClick={onClose} style={{ padding: '0.3rem 0.6rem' }}>×</button>
          </div>
        </div>

        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)',
          display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '0.75rem' }}>
          <div>
            <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>Склад</label>
            <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} style={{ width: '100%' }}>
              <option value="">— Усі склади —</option>
              {warehousesQ.data?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>Вид номенклатури</label>
            <select value={kindId} onChange={(e) => setKindId(e.target.value)} style={{ width: '100%' }}>
              <option value="">— Усі види —</option>
              {(kindsQ.data ?? []).filter((k) => !k.deletion_mark).map((k) => (
                <option key={k.id} value={k.id}>{k.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>Пошук (назва, код, артикул)</label>
            <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Почніть набирати назву товару…" style={{ width: '100%' }} />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.85rem' }}>
            {chk('лише з залишком у цій організації', onlyWithStock, setOnlyWithStock)}
            {chk('в дорозі', chkTransit, setChkTransit, !onlyWithStock, 'Показати колонку і товари, замовлені постачальникам')}
            {chk('в резерві', chkReserved, setChkReserved, !onlyWithStock, 'Показати колонку і товари з резервом під рахунки')}
            {chk('замовлено', chkOrdered, setChkOrdered, !onlyWithStock, 'Показати колонку і товари, замовлені покупцями')}
          </div>
          {priceTypes.length > 0 && (
            <div style={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.85rem' }}>
              <span style={{ color: 'var(--muted)' }}>Ціни:</span>
              {priceTypes.map((pt) => chk(pt.name, priceTypeIdsRaw.includes(pt.id),
                (v) => setPriceTypeIds((ids) => v ? [...ids, pt.id] : ids.filter((x) => x !== pt.id)),
                !onlyWithStock, pt.is_purchase ? PURCHASE_METHOD_TIP[pt.purchase_calc_method ?? 'LAST']
                  : 'Показати колонку цін; клік по ціні додасть товар з нею'))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}><GridMoreRightButton more={more} /></div>
        <div className="ag-theme-quartz" style={{ height: '55vh', minHeight: 280 }}>
          <AgGridReact<SellablePickerRow>
            rowModelType="infinite"
            datasource={datasource}
            cacheBlockSize={100}
            columnDefs={cols}
            defaultColDef={{ sortable: true, resizable: true }}
            {...more.gridProps}
            onGridReady={(e) => { gridApiRef.current = e.api; more.gridProps.onGridReady(e); }}
            rowHeight={28}
            headerHeight={30}
            getRowStyle={(p) => (requireStock && p.data && Number(p.data.quantity) <= 0
              ? { cursor: 'not-allowed', color: '#94a3b8' } : { cursor: 'pointer', color: 'inherit' })}
            onCellClicked={(e) => {
              const r = e.data;
              if (!r) return;
              // ч.927: реалізація — лише те, що фізично є на складі.
              if (requireStock && Number(r.quantity) <= 0) {
                showToastImperative({ type: 'warning', durationMs: 5000,
                  message: 'Товару немає на складі — реалізувати не можна. Товар у дорозі можна виставити в рахунок покупцю.' });
                return;
              }
              const colId = e.column.getColId();
              const price = colId.startsWith('price_') && e.value != null && Number(e.value) > 0
                ? String(e.value) : undefined;
              const row: GoodsBalanceRow = {
                warehouse_id: r.warehouse_id ?? '', warehouse_code: r.warehouse_code ?? '',
                warehouse_name: r.warehouse_name ?? '', nomenclature_id: r.nomenclature_id,
                nomenclature_code: r.nomenclature_code, nomenclature_article: r.nomenclature_article,
                nomenclature_name: r.nomenclature_name, quantity: r.quantity, cost: null,
              };
              // Мультивибір: клік по рядку — позначити/зняти; клік по ціні — одразу
              // додати цей товар з цією ціною.
              if (multiSelect && price === undefined) {
                const key = `${row.warehouse_id}-${row.nomenclature_id}`;
                const m = selectedRef.current;
                if (m.has(key)) m.delete(key); else m.set(key, row);
                setSelectedCount(m.size);
                e.api.refreshCells({ columns: ['_sel'], force: true });
                return;
              }
              onPick(row, price);
            }}
          />
        </div>

        <div style={{ padding: '0.6rem 1rem', borderTop: '1px solid var(--border)',
          fontSize: '0.85rem', color: 'var(--muted)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span>
              {total !== null && <>Знайдено: <strong>{total}</strong>{' • '}</>}
              {multiSelect
                ? 'Клік по рядку — позначити; клік по ціні — одразу додати з цією ціною; заголовок — сортування'
                : 'Клік по рядку — додати товар без ціни; клік по ціні — додати з цією ціною; заголовок — сортування'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
